import axiosInstance from './axiosInstance';

function authHeaders() {
  const token = localStorage.getItem('accessToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function dashboardRequest(method, url, data, options = {}) {
  const idempotencyKey = options.idempotencyKey || data?.idempotencyKey;
  const isMultipart = Object.values(data || {}).some(hasFileValue);
  const payload = isMultipart ? toFormData(data) : data;
  const headers = { ...authHeaders() };
  if (!isMultipart) headers['Content-Type'] = 'application/json';
  if (idempotencyKey) headers['Idempotency-Key'] = idempotencyKey;

  return axiosInstance.request({
    method,
    url,
    data: payload,
    headers,
    withCredentials: true,
  });
}

function hasFileValue(value) {
  if (!value) return false;
  if (typeof File !== 'undefined' && value instanceof File) return true;
  if (typeof Blob !== 'undefined' && value instanceof Blob) return true;
  if (Array.isArray(value)) return value.some(hasFileValue);
  return false;
}

function toFormData(data) {
  const form = new FormData();
  Object.entries(data || {}).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (item !== undefined && item !== null && item !== '') form.append(key, item);
      });
      return;
    }
    form.append(key, value);
  });
  return form;
}

function contractorPost(token, path, data) {
  const payload = Object.values(data || {}).some(hasFileValue) ? toFormData(data) : data;
  const idempotencyKey = data?.idempotencyKey;
  const headers = idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : undefined;
  return axiosInstance.post(`/api/jobs/${encodeURIComponent(token)}${path}`, payload, { headers });
}

export async function getJobs() {
  const res = await dashboardRequest('GET', '/api/dashboard/jobs');
  return Array.isArray(res?.data) ? res.data : [];
}

export async function createJob(data) {
  const res = await dashboardRequest('POST', '/api/dashboard/jobs', data);
  return res?.data ?? null;
}

export async function updateJob(id, data) {
  const res = await dashboardRequest('PUT', `/api/dashboard/jobs/${id}`, data);
  return res?.data ?? null;
}

export async function deleteJob(id) {
  await dashboardRequest('DELETE', `/api/dashboard/jobs/${id}`);
  return true;
}

export async function assignJobWorker(id, data) {
  const res = await dashboardRequest('POST', `/api/dashboard/jobs/${id}/assign-worker`, data);
  return res?.data ?? null;
}

export async function assignJobContractor(id, contractorId, options = {}) {
  const res = await dashboardRequest('PATCH', `/api/dashboard/jobs/${id}/assign-contractor`, {
    contractorId,
    idempotencyKey: options.idempotencyKey,
  }, options);
  return res?.data ?? null;
}

export async function reviewJobQuote(id, action, notes, options = {}) {
  const res = await dashboardRequest('POST', `/api/dashboard/jobs/${id}/quote-review`, {
    action,
    notes,
    idempotencyKey: options.idempotencyKey,
  }, options);
  return res?.data ?? null;
}

export async function updateJobQuoteRequirement(id, quoteRequired, notes, options = {}) {
  const res = await dashboardRequest('POST', `/api/dashboard/jobs/${id}/quote-requirement`, {
    quoteRequired,
    notes,
    idempotencyKey: options.idempotencyKey,
  }, options);
  return res?.data ?? null;
}

export async function scheduleJob(id, scheduledDate, options = {}) {
  const payload = typeof scheduledDate === 'object' && scheduledDate !== null
    ? scheduledDate
    : { scheduledDate };
  const res = await dashboardRequest('POST', `/api/dashboard/jobs/${id}/schedule`, {
    ...payload,
    idempotencyKey: options.idempotencyKey || payload.idempotencyKey,
  }, options);
  return res?.data ?? null;
}

export async function reviewJobProof(id, action, notes, options = {}) {
  const res = await dashboardRequest('POST', `/api/dashboard/jobs/${id}/proof-review`, {
    action,
    notes,
    idempotencyKey: options.idempotencyKey,
  }, options);
  return res?.data ?? null;
}

export async function fetchContractorJob(token) {
  const res = await axiosInstance.get(`/api/jobs/${encodeURIComponent(token)}`);
  return res?.data ?? null;
}

export async function acceptContractorJob(token, data = {}) {
  const res = await contractorPost(token, '/accept', data);
  return res?.data ?? null;
}

export async function declineContractorJob(token, reason, data = {}) {
  const res = await contractorPost(token, '/decline', {
    ...data,
    reason,
  });
  return res?.data ?? null;
}

export async function submitContractorQuote(token, data) {
  const res = await contractorPost(token, '/quote', data);
  return res?.data ?? null;
}

export async function proposeContractorDate(token, proposedDate, extra = {}) {
  const payload = typeof proposedDate === 'object' && proposedDate !== null
    ? proposedDate
    : { proposedDate, ...extra };
  const res = await contractorPost(token, '/propose-date', payload);
  return res?.data ?? null;
}

export async function updateContractorProgress(token, action, notes, data = {}) {
  const res = await contractorPost(token, '/progress', {
    ...data,
    action,
    notes,
  });
  return res?.data ?? null;
}

export async function submitContractorProof(token, data) {
  const res = await contractorPost(token, '/proof', data);
  return res?.data ?? null;
}

export async function openJobAttachment(jobId, attachmentId) {
  const res = await dashboardRequest(
    'GET',
    `/api/dashboard/jobs/${idEncode(jobId)}/attachments/${idEncode(attachmentId)}/open`
  );
  return res?.data?.url || '';
}

export async function openContractorJobAttachment(token, attachmentId) {
  const res = await axiosInstance.get(
    `/api/jobs/${encodeURIComponent(token)}/attachments/${encodeURIComponent(attachmentId)}/open`
  );
  return res?.data?.url || '';
}

export async function updateContractorPreference(token, data) {
  const res = await axiosInstance.patch(`/api/jobs/${encodeURIComponent(token)}/contractor-preference`, data);
  return res?.data ?? null;
}

function idEncode(value) {
  return encodeURIComponent(value);
}
